/**
 * Maps React Router paths to stable route names for analytics.
 */

const ROUTE_DEFINITIONS = [
  { pattern: /^\/$/, name: "home", title: "Home" },
  { pattern: /^\/professionals\/?$/, name: "professionals", title: "For Professionals" },
  { pattern: /^\/contact\/?$/, name: "contact", title: "Contact Us" },
  {
    pattern: /^\/enquiry\/questions\/[^/]+\/?$/,
    name: "enquiry_questions",
    title: "Enquiry Questions",
  },
  { pattern: /^\/enquiry\/success\/?$/, name: "enquiry_success", title: "Requirement Published" },
  { pattern: /^\/requirement-success\/?$/, name: "requirement_success", title: "Requirement Published" },
  { pattern: /^\/enquiry\/?$/, name: "enquiry", title: "Post Requirement" },
  { pattern: /^\/create-business\/success\/?$/, name: "create_business_success", title: "Create Business Success" },
  { pattern: /^\/create-business\/profile\/?$/, name: "create_business_profile", title: "Create Business Profile" },
  { pattern: /^\/create-business\/?$/, name: "create_business", title: "Create Business" },
  { pattern: /^\/claim\/[^/]+\/?$/, name: "claim_account", title: "Claim Account" },
  { pattern: /^\/careers\/[^/]+\/?$/, name: "job_detail", title: "Job Detail" },
  { pattern: /^\/careers\/?$/, name: "careers", title: "Careers" },
  { pattern: /^\/privacy-policy\/?$/, name: "privacy_policy", title: "Privacy Policy" },
  { pattern: /^\/terms\/?$/, name: "terms", title: "Terms of Service" },
  { pattern: /^\/cookie-policy\/?$/, name: "cookie_policy", title: "Cookie Policy" },
  { pattern: /^\/takedown-policy\/?$/, name: "takedown_policy", title: "Takedown Policy" },
  { pattern: /^\/downloads\/?$/, name: "downloads", title: "Downloads" },
];

/** Ads categoryParam → analytics page_name / page_title */
const ADS_CATEGORY_PAGE_TITLES = {
  "1": "1 - Interior-Requirement",
  "2": "2 - Architecture-Requirement",
  "5": "5 - Vastu-Requirement",
  "6": "6 - Automation-Requirement",
};

const ADS_CATEGORY_PARAM_ALIASES = {
  interior: "1",
  interior_design: "1",
  interiors: "1",
  architecture: "2",
  architect: "2",
  vastu: "5",
  automation: "6",
  furniture: "3",
};

/** categoryParam / id → GA4 event prefix (e.g. vastu_post_requirement_start) */
const ADS_CATEGORY_EVENT_SLUG_BY_ID = {
  "1": "interior",
  "2": "architecture",
  "3": "furniture",
  "5": "vastu",
  "6": "automation",
};

function pathnameOnly(path = "") {
  const base = (path || "").split("?")[0].split("#")[0];
  return base.endsWith("/") && base.length > 1 ? base.slice(0, -1) : base || "/";
}

export function normalizeCategoryParam(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/%20/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function extractEnquiryCategoryParam(pathname = "") {
  const path = pathnameOnly(pathname);
  const match = path.match(/\/enquiry\/questions\/([^/]+)/i);
  if (!match?.[1]) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function resolveAdsCategoryId(rawHint = "") {
  const hint = String(rawHint || "").trim();
  if (!hint) return "";

  if (ADS_CATEGORY_PAGE_TITLES[hint]) return hint;

  const normalized = normalizeCategoryParam(hint);
  if (ADS_CATEGORY_PAGE_TITLES[normalized]) return normalized;

  if (ADS_CATEGORY_PARAM_ALIASES[normalized]) {
    return ADS_CATEGORY_PARAM_ALIASES[normalized];
  }

  return "";
}

/** Event slug for ads funnels: vastu, interior, architecture, automation, … */
export function getAdsCategoryEventSlug(categoryParam = "") {
  const categoryId = resolveAdsCategoryId(categoryParam);
  if (categoryId && ADS_CATEGORY_EVENT_SLUG_BY_ID[categoryId]) {
    return ADS_CATEGORY_EVENT_SLUG_BY_ID[categoryId];
  }

  const normalized = normalizeCategoryParam(categoryParam);
  if (!normalized) return "ads";

  if (ADS_CATEGORY_EVENT_SLUG_BY_ID[normalized]) {
    return ADS_CATEGORY_EVENT_SLUG_BY_ID[normalized];
  }

  if (ADS_CATEGORY_PARAM_ALIASES[normalized]) {
    const id = ADS_CATEGORY_PARAM_ALIASES[normalized];
    return ADS_CATEGORY_EVENT_SLUG_BY_ID[id] || normalized;
  }

  return normalized;
}

/**
 * Builds ads GA4 event names from categoryParam.
 * e.g. buildAdsRequirementEventName("5", "start") → vastu_post_requirement_start
 */
export function buildAdsRequirementEventName(categoryParam = "", action = "start") {
  const slug = getAdsCategoryEventSlug(categoryParam);
  const suffix = String(action || "start").trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
  return `${slug}_post_requirement_${suffix}`.slice(0, 40);
}

/** Page title for ads requirement flows from categoryParam (URL segment or query). */
export function getAdsRequirementPageTitle(categoryParam = "") {
  const categoryId = resolveAdsCategoryId(categoryParam);
  if (categoryId && ADS_CATEGORY_PAGE_TITLES[categoryId]) {
    return ADS_CATEGORY_PAGE_TITLES[categoryId];
  }

  const normalized = normalizeCategoryParam(categoryParam);
  if (!normalized) return "Ads Post Requirement";

  const label = normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return `${label}-Requirement`;
}

export function getRouteInfo(pathname = "", search = "", { categoryParam = "" } = {}) {
  const path = pathnameOnly(pathname);
  const match = ROUTE_DEFINITIONS.find((route) => route.pattern.test(path));

  const paramFromPath = extractEnquiryCategoryParam(path);
  const resolvedCategoryParam = categoryParam || paramFromPath;
  const adsPageTitle = resolvedCategoryParam
    ? getAdsRequirementPageTitle(resolvedCategoryParam)
    : "";

  let route_name = match?.name || "unknown";
  let route_title = match?.title || "Unknown Page";

  if (adsPageTitle && (match?.name === "enquiry_questions" || match?.name === "requirement_success")) {
    route_title = adsPageTitle;
    route_name = `ads_${normalizeCategoryParam(resolvedCategoryParam) || "requirement"}`;
  }

  return {
    route_path: `${path}${search || ""}`,
    route_name,
    route_title,
    page_path: `${path}${search || ""}`,
    ...(resolvedCategoryParam ? { category_param: resolvedCategoryParam } : {}),
  };
}

export function listTrackedRoutes() {
  return ROUTE_DEFINITIONS.map((route) => ({
    name: route.name,
    title: route.title,
  }));
}

export function listAdsCategoryPageTitles() {
  return { ...ADS_CATEGORY_PAGE_TITLES };
}
